import React, { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert
} from '@mui/material';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { UserData } from '../UserAndProfile/UserContext'; // For type reference if needed
      // TODO: Implement referral request section: "Know anyone else who'd be interested? Refer them for $10 credit!"


interface StudentProfileFormProps {
  uid: string;
  onComplete: () => void;
  userRole: UserData['userRole'];
  isParentLoading: boolean; // Add prop for parent loading state
}

interface StudentProfileFormData {
  hasTakenSAT: boolean;
  previousSATScores: { math: string; readingWriting: string };
  targetSATScores: { math: string; readingWriting: string };
  // upcomingTestDate: string; // Comment out upcomingTestDate
}

interface FormErrors {
  previousSatMath?: string;
  previousSatReadingWriting?: string;
  targetSatMath?: string;
  targetSatReadingWriting?: string;
  // upcomingTestDate?: string; // No specific errors were defined for it, but if they were, comment them out
  submit?: string;
}

const StudentProfileForm: React.FC<StudentProfileFormProps> = ({ uid, onComplete, userRole, isParentLoading }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState<StudentProfileFormData>({
    hasTakenSAT: false,
    previousSATScores: { math: '', readingWriting: '' },
    targetSATScores: { math: '', readingWriting: '' },
    // upcomingTestDate: '', // Comment out initialization
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateSATScore = (score: string): boolean => {
    if (!score) return true; // Optional scores are valid if empty
    const numScore = parseInt(score, 10);
    return !isNaN(numScore) && numScore >= 200 && numScore <= 800;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = event.target;
    
    if (name === 'previousSatMath') {
      setFormData(prev => ({ ...prev, previousSATScores: { ...prev.previousSATScores, math: value } }));
    } else if (name === 'previousSatReadingWriting') {
      setFormData(prev => ({ ...prev, previousSATScores: { ...prev.previousSATScores, readingWriting: value } }));
    } else if (name === 'targetSatMath') {
      setFormData(prev => ({ ...prev, targetSATScores: { ...prev.targetSATScores, math: value } }));
    } else if (name === 'targetSatReadingWriting') {
      setFormData(prev => ({ ...prev, targetSATScores: { ...prev.targetSATScores, readingWriting: value } }));
    // } else if (name === 'upcomingTestDate') { // Comment out this block
    //   setFormData(prev => ({ ...prev, upcomingTestDate: value }));
    }
    // Clear specific errors when input changes
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
    if (errors.submit) {
        setErrors(prev => ({ ...prev, submit: undefined }));
    }
  };

  const handleScoreBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;

    // Only proceed if the value is not empty
    if (value.trim() !== '') {
      let score = parseFloat(value);

      if (!isNaN(score)) { // Check if parsing was successful
        let roundedScore = Math.round(score / 10) * 10;
        // Clamp the score to be within the 200-800 range
        roundedScore = Math.max(200, Math.min(800, roundedScore));

        const updatedValue = roundedScore.toString();

        // Update state only if the rounded value is different from the current input value
        // to avoid unnecessary re-renders if the value is already rounded or doesn't need clamping.
        if (updatedValue !== value) {
          if (name === 'targetSatMath') {
            setFormData(prev => ({ ...prev, targetSATScores: { ...prev.targetSATScores, math: updatedValue } }));
          } else if (name === 'targetSatReadingWriting') {
            setFormData(prev => ({ ...prev, targetSATScores: { ...prev.targetSATScores, readingWriting: updatedValue } }));
          } else if (name === 'previousSatMath' && formData.hasTakenSAT) {
            setFormData(prev => ({ ...prev, previousSATScores: { ...prev.previousSATScores, math: updatedValue } }));
          } else if (name === 'previousSatReadingWriting' && formData.hasTakenSAT) {
            setFormData(prev => ({ ...prev, previousSATScores: { ...prev.previousSATScores, readingWriting: updatedValue } }));
          }
        }
      }
      // If value.trim() !== '' but parseFloat(value) is NaN (e.g., "abc"),
      // we do nothing here. Validation will catch it.
    }
    // If value.trim() === '', do nothing. handleInputChange has already set it to ''.
    // Validation will catch required empty fields.
  };

  const handleHasTakenSATChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string | null // Value of the selected ToggleButton ('yes' or 'no')
  ) => {
    if (newValue !== null) { // Ensure a selection is made
      const userHasTakenSAT = newValue === 'yes';
      setFormData(prev => ({
        ...prev,
        hasTakenSAT: userHasTakenSAT,
        // Clear previous scores if "No" is selected
        previousSATScores: !userHasTakenSAT ? { math: '', readingWriting: '' } : prev.previousSATScores
      }));
      if (!userHasTakenSAT) {
          // Clear errors for previous scores if "No" is selected
          setErrors(prev => ({...prev, previousSatMath: undefined, previousSatReadingWriting: undefined}));
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    // Validate previous scores only if hasTakenSAT is true and scores are entered
    if (formData.hasTakenSAT) {
      if (formData.previousSATScores.math && !validateSATScore(formData.previousSATScores.math)) {
        newErrors.previousSatMath = 'Score must be between 200-800.';
      }
      if (formData.previousSATScores.readingWriting && !validateSATScore(formData.previousSATScores.readingWriting)) {
        newErrors.previousSatReadingWriting = 'Score must be between 200-800.';
      }
    }

    // Validate target scores (required)
    if (!formData.targetSATScores.math.trim()) {
      newErrors.targetSatMath = 'Target Math score is required.';
    } else if (!validateSATScore(formData.targetSATScores.math)) {
      newErrors.targetSatMath = 'Score must be between 200-800.';
    }

    if (!formData.targetSATScores.readingWriting.trim()) {
      newErrors.targetSatReadingWriting = 'Target Reading/Writing score is required.';
    } else if (!validateSATScore(formData.targetSATScores.readingWriting)) {
      newErrors.targetSatReadingWriting = 'Score must be between 200-800.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    // Small delay to ensure loading state is visible
    await new Promise(resolve => setTimeout(resolve, 100));
    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }
    
    setErrors({});

    const profileDataToUpdate: Partial<any> = { // Using 'any' for UserData fields for simplicity here
      hasTakenSAT: formData.hasTakenSAT,
      targetScores: {
        math: `${parseInt(formData.targetSATScores.math, 10) || 600}`,
        readingWriting: `${parseInt(formData.targetSATScores.readingWriting, 10) || 600}`,
      },
      profileStep3Completed: true,
    };

    if (formData.hasTakenSAT) {
      profileDataToUpdate.previousSATScores = {
        math: formData.previousSATScores.math ? `${parseInt(formData.previousSATScores.math, 10)}` : null,
        readingWriting: formData.previousSATScores.readingWriting ? `${parseInt(formData.previousSATScores.readingWriting, 10)}` : null,
      };
    } else {
        profileDataToUpdate.previousSATScores = { math: null, readingWriting: null };
    }

    // Comment out upcomingTestDate logic
    // if (formData.upcomingTestDate) {
    //   profileDataToUpdate.upcomingTestDate = formData.upcomingTestDate;
    // } else {
    //   profileDataToUpdate.upcomingTestDate = null;
    // }

    try {
      const userDocRef = doc(db, 'users', uid);
      await updateDoc(userDocRef, profileDataToUpdate);
      // TODO: Implement referral request section: "Know anyone else who'd be interested? Refer them for $10 credit!"
      // TODO: Implement selectable areas of improvement (e.g., geometry, punctuation) using checkboxes or a multi-select component. Store as an array in Firestore.
      onComplete();
    } catch (error) {
      console.error("Error updating student profile:", error);
      setErrors({ submit: 'Failed to save profile information. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{marginTop: 2}}> 
      <Card sx={{ p: { xs: 2, sm: 3 }, width: '100%', maxWidth: '600px', margin: 'auto' }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom textAlign="center">
            SAT Profile Information
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
            Help us tailor your experience. Target scores help us design your personalized SAT prep plan.
          </Typography>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" gutterBottom id="has-taken-sat-label">
                  {userRole === 'parent' ? "Has your student taken the SAT before?" :
                  "Have you taken the SAT before?"}
                </Typography>
                <ToggleButtonGroup
                  value={formData.hasTakenSAT ? 'yes' : 'no'}
                  exclusive
                  onChange={handleHasTakenSATChange}
                  aria-labelledby="has-taken-sat-label"
                  fullWidth
                  sx={{ 
                    mt: 0.5,
                    display: 'flex', // Ensure buttons are on the same line
                  }}
                >
                  <ToggleButton 
                    value="yes" 
                    aria-label="Yes"
                    sx={{
                      flexGrow: 1, // Make buttons take equal space
                      textTransform: 'none', // from muiTheme
                      borderRadius: '10px', // from muiTheme
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main', // from muiTheme
                        color: 'white', // from muiTheme
                        '&:hover': {
                          backgroundColor: 'primary.dark', // from muiTheme
                        },
                      },
                      // Optional: Style for unselected to look like outlined
                      '&:not(.Mui-selected)': {
                         color: 'primary.main',
                         borderColor: theme.palette.primary.main, // Use theme for consistency
                         '&:hover': {
                           backgroundColor: theme.palette.action.hover, // Default hover for outlined-like
                         }
                      }
                    }}
                  >
                    Yes
                  </ToggleButton>
                  <ToggleButton 
                    value="no" 
                    aria-label="No"
                    sx={{
                      flexGrow: 1, // Make buttons take equal space
                      textTransform: 'none', // from muiTheme
                      borderRadius: '10px', // from muiTheme
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main', // from muiTheme
                        color: 'white', // from muiTheme
                        '&:hover': {
                          backgroundColor: 'primary.dark', // from muiTheme
                        },
                      },
                      '&:not(.Mui-selected)': {
                        color: 'primary.main',
                        borderColor: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        }
                     }
                    }}
                  >
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              

              {formData.hasTakenSAT && (
                <Stack spacing={2} sx={{ pl: 2, borderLeft: `2px solid ${theme.palette.divider}`, py:1, mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Previous SAT Scores (Optional)</Typography>
                  <TextField
                    label="Previous Math Score"
                    name="previousSatMath"
                    type="number"
                    value={formData.previousSATScores.math}
                    onChange={handleInputChange}
                    onBlur={handleScoreBlur}
                    error={!!errors.previousSatMath}
                    helperText={errors.previousSatMath || "200-800"}
                    fullWidth
                    inputProps={{ min: 200, max: 800, step: 10 }}
                  />
                  <TextField
                    label="Previous Reading & Writing Score"
                    name="previousSatReadingWriting"
                    type="number"
                    value={formData.previousSATScores.readingWriting}
                    onChange={handleInputChange}
                    onBlur={handleScoreBlur}
                    error={!!errors.previousSatReadingWriting}
                    helperText={errors.previousSatReadingWriting || "200-800"}
                    fullWidth
                    inputProps={{ min: 200, max: 800, step: 10 }}
                  />
                </Stack>
              )}

              {/* Comment out Upcoming Test Date TextField */}
              {/*
              <TextField
                label="Upcoming Test Date (Optional)"
                name="upcomingTestDate"
                type="date"
                value={formData.upcomingTestDate}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
              */}

              <Typography variant="subtitle1" gutterBottom sx={{pt:1}}>
                {userRole === 'parent' ? "What are your student's target SAT scores?" : "What SAT scores do you want to achieve?"}
              </Typography>
              <TextField
                required
                label="Target Math Score"
                name="targetSatMath"
                type="number"
                value={formData.targetSATScores.math}
                onChange={handleInputChange}
                onBlur={handleScoreBlur}
                error={!!errors.targetSatMath}
                helperText={errors.targetSatMath || "200-800"}
                fullWidth
                inputProps={{ min: 200, max: 800, step: 10 }}
              />
              <TextField
                required
                label="Target Reading & Writing Score"
                name="targetSatReadingWriting"
                type="number"
                value={formData.targetSATScores.readingWriting}
                onChange={handleInputChange}
                onBlur={handleScoreBlur}
                error={!!errors.targetSatReadingWriting}
                //helperText={errors.targetSatReadingWriting || "200-800"}
                fullWidth
                inputProps={{ min: 200, max: 800, step: 10 }}
              />
              
              {errors.submit && (
                  <Alert severity="error" sx={{ mt: 2 }}>{errors.submit}</Alert>
              )}

              <Button type="submit" variant="contained" color="primary"
                fullWidth disabled={isSubmitting || isParentLoading}
                sx={{ mt: 3, py: 1.5, textTransform: 'none', fontSize: '1rem' }}
              >
                {isSubmitting || isParentLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  <span>{isSubmitting ? 'Saving Profile...' : 'Setting up your account...'}</span>
                </Box>
                ) : 'Complete Profile'/*Formerly Save Profile & Continue*/}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StudentProfileForm;